/* eslint-disable max-lines */
import {observer} from 'mobx-react-lite'
import * as React from 'react'
import {Lang} from 'yougile-lang'
import {ArrUtil, logger, ObjUtil, Time, Util} from 'yougile-util'
import {
  DataObjectId,
  extractStickerState,
  getColumnColorHex,
  getColumnColorNumber,
  isSetSticker,
  TaskData
} from 'yougile-model'
import {boardViewStore} from '@components/board-view'
import {globals} from '@extensions/globals-base'
import {Task as TaskExt} from '@extensions/task'
import {Debouncer} from '../../../common/debouncer'
import {DragData} from '../../../common/drag-data'
import {DivWithHint, Hint} from '../common/hint/hint'
import {Arrow, ArrowPoint} from './arrow'
import {Task, TaskAssigned} from './task'
import {TimelinePosition} from './timeline-position'

const baseDayHeight = 48
const baseDayHeightSpace = 16
const baseDayWidth = 60

const day = 24 * 3600_000

const scrollDebouncer = new Debouncer(500)
const moveDebouncer = new Debouncer(35)
let latestMoveDeadline: number

let container: HTMLElement

let tasksContainer: HTMLElement
const tasksContainerRef = (el: HTMLElement) => (tasksContainer = el)

let tasksLine: HTMLElement
const tasksLineRef = (el: HTMLElement) => (tasksLine = el)

let grabbing = false

export interface Props {
  className?: string
  withoutUnscheduled?: boolean
}

export interface InnerProps {
  tasks: TaskData[]
  taskClick: (taskId: DataObjectId) => () => void
  applyChange: (next: () => void) => void
}

export interface State {
  start: number
  stop: number
  dragPoint: number
  changedTask: TaskData
  highlight: [number, number] // startDate, deadline
  maxUnscheduled: number
  hoveredTaskId: DataObjectId
}

export interface Handlers {
  setStart?: (time: number) => State
  setStop?: (time: number) => State
  setChangedTask?: (
    task: TaskData | undefined,
    dragPoint: number | undefined
  ) => State
  setHighlight?: (range: [number, number], hoveredTaskId: DataObjectId) => State
  setMaxUnscheduled?: (n: number) => State
}

export const TimelineBase = observer(
  // eslint-disable-next-line max-lines-per-function
  (props: Props & InnerProps & State & Handlers) => {
    const {
      className,
      start,
      stop,
      tasks,
      taskClick,
      setChangedTask,
      changedTask,
      applyChange,
      dragPoint,
      highlight,
      setHighlight,
      setStart,
      setStop,
      maxUnscheduled,
      setMaxUnscheduled,
      hoveredTaskId,
      withoutUnscheduled
    } = props
    const bem = Util.bemFrom('timeline', [className ?? ''])

    const {zoom} = boardViewStore.state
    const today = Time.middleOfDay(Time.now())
    const dayWidth = Math.round(baseDayWidth * zoom)
    const dayHeight = Math.round(baseDayHeight * zoom)
    const dayHeightSpace = Math.round(baseDayHeightSpace * zoom)

    const days = Math.ceil((stop - start) / day)
    const now = Time.now()
    const dates: React.ReactNode[] = Util.range(days).map(n => {
      const d = Time.toLocalDate(start + n * day)
      return (
        <div
          className={bem('date', {
            today: Time.sameDates(d.getTime(), now)
          })}
          key={d.getTime()}
        >
          {d.getDate()}
        </div>
      )
    })
    const months: React.ReactNode[] = []
    let curr = -1
    Util.range(days).forEach(n => {
      const t = start + n * day
      const d = Time.toLocalDate(t)
      const monthN = d.getMonth()
      const year =
        d.getFullYear() !== new Date().getFullYear() ? d.getFullYear() : ''
      if (curr !== monthN) {
        curr = monthN
        const dayCount = Math.round(
          (Time.endOfDay(Math.min(Time.endOfMonth(t), stop)) -
            Time.endOfDay(t)) /
            day
        )
        const width = dayWidth * dayCount + 'px'
        months.push(
          <div className={bem('month')} style={{width}} key={t}>
            {Lang.msg.monthNames[monthN]} {year}
          </div>
        )
      }
    })

    const highlightStart = changedTask
      ? extractStickerState(changedTask.startDate) ||
        extractStickerState(changedTask.deadline)
      : highlight?.[0]
    const highlightEnd = changedTask
      ? extractStickerState(changedTask.deadline)
      : highlight?.[1]

    const highlightedArea =
      highlightStart && highlightEnd ? (
        <div
          className={bem('highlighted')}
          style={{
            left:
              dayWidth *
                Math.round((Time.middleOfDay(highlightStart) - start) / day) +
              'px',
            width:
              dayWidth *
                Math.round(
                  (Time.middleOfDay(highlightEnd) -
                    Time.middleOfDay(highlightStart) +
                    day) /
                    day
                ) +
              'px'
          }}
        />
      ) : undefined

    const weekends: React.ReactNode[] = []
    const verticalLines: React.ReactNode[] = []
    Util.range(days).forEach(n => {
      const d = Time.toLocalDate(start + n * day)
      const wd = d.getDay()
      const left = n * dayWidth + 'px'
      verticalLines.push(
        <div
          className={bem('v-line')}
          key={d.getTime()}
          style={{
            left
          }}
        />
      )
      if (wd === 0 || wd === 6) {
        weekends.push(
          <div
            className={bem('weekend')}
            key={d.getTime()}
            style={{
              width: dayWidth + 'px',
              left
            }}
          />
        )
      }
    })

    const todayPos =
      Math.round((dayWidth * (today - start)) / day + 0.5 * dayWidth) + 'px'

    const totalWidth = days * dayWidth

    const setScroll = (el: HTMLElement) => {
      if (!el) {
        return
      }

      if (!container || container !== el) {
        container = el
        const width = container.getBoundingClientRect().width
        container.scrollLeft = (totalWidth - width) / 2
      }
    }

    const onScroll = () => {
      if (grabbing) {
        return
      }
      scrollDebouncer.runButNotOften(() => {
        if (!container || !tasksLine) {
          return
        }
        const zoom = +(window.getComputedStyle(tasksLine) as any).zoom || 1
        const pos = container.scrollLeft
        if (pos <= 10) {
          try {
            container.classList.add(bem('no-scroll'))
          } catch (e) {
            logger.warn(e)
            return
          }

          container.scrollLeft = 20 * dayWidth
          setStart?.(start - 20 * day)

          setTimeout(() => {
            requestAnimationFrame(() => {
              try {
                container.classList.remove(bem('no-scroll'))
              } catch (e) {
                logger.warn(e)
              }
            })
          }, 200)
        } else if (pos + container.clientWidth >= totalWidth * zoom - 10) {
          setStop?.(stop + 20 * day)
        }
      })
    }

    const taskItems: React.ReactNode[] = []

    const dragStart = (task: TaskData) => (e: React.DragEvent) => {
      let point = 0
      DragData.set(e, {type: 'empty'})
      if (e.currentTarget) {
        const rect = e.currentTarget.getBoundingClientRect()
        point = e.clientX - rect.right
      }
      Hint.hide()
      setTimeout(
        () =>
          requestAnimationFrame(() => {
            if (isSetSticker(task.deadline)) {
              setChangedTask?.(Util.clone(task), point)
            } else {
              setChangedTask?.(
                {
                  ...Util.clone(task),
                  deadline: Time.middleOfDay(now),
                  startDate: Time.middleOfDay(now - 2 * day),
                  timelineDepth: 5
                },
                point
              )
            }
            setTimeout(() => Hint.hide(), 100)
          }),
        100
      )
    }
    const dragEnd = () => {
      applyChange(() => {
        setTimeout(() => {
          requestAnimationFrame(() => {
            setChangedTask?.(undefined, undefined)
          })
        }, 300)
      })
    }

    const schedTasks = Util.removeDuplicateIds(tasks).filter(
      t =>
        isSetSticker(t.deadline) &&
        ((t.deadline < stop && t.deadline > start) ||
          (isSetSticker(t.startDate) &&
            !(
              (t.deadline < start && t.startDate < start) ||
              (t.deadline > stop && t.startDate > stop)
            )))
    )

    if (changedTask && !schedTasks.find(t => t.id === changedTask.id)) {
      schedTasks.push(changedTask)
    }

    const getClientLeft = () =>
      container.scrollLeft - container.getBoundingClientRect().left

    const positions = TimelinePosition.getPositions(
      changedTask
        ? schedTasks.map(t => (t.id === changedTask.id ? changedTask : t))
        : schedTasks,
      changedTask ? changedTask.id : undefined
    )

    const getTaskPoint = (task: TaskData, end = false): ArrowPoint => {
      const deadline = extractStickerState(task.deadline)
      const startDate = extractStickerState(task.startDate)
      const taskStart = Time.middleOfDay(startDate || deadline)
      const position = positions.get(task.id)
      const depth = position.depth
      const taskWidth = startDate
        ? Math.round(
            (Time.middleOfDay(deadline) - Time.middleOfDay(startDate) + day) /
              day
          ) * dayWidth
        : dayWidth
      return {
        x:
          Math.round((dayWidth * (taskStart - Time.middleOfDay(start))) / day) +
          (end ? taskWidth : 0),
        y: depth * (dayHeight + dayHeightSpace) + 24 + dayHeight * 0.5
      }
    }
    const arrows: React.ReactNode[] = []
    const hoveredDependencies: Set<DataObjectId> = new Set()
    const index = ArrUtil.createIndex(schedTasks, t => t.id)
    schedTasks.forEach(t => {
      ObjUtil.forEach(t.dependencies, (id: DataObjectId) => {
        if (!index.has(id)) {
          return
        }
        const to = changedTask && changedTask.id === t.id ? changedTask : t
        const from =
          changedTask && changedTask.id === index.get(id).id
            ? changedTask
            : index.get(id)
        if (t.id === hoveredTaskId) {
          hoveredDependencies.add(id)
        } else if (id === hoveredTaskId) {
          hoveredDependencies.add(t.id)
        }

        arrows.push(
          <Arrow
            key={t.id + id}
            from={getTaskPoint(from, true)}
            to={getTaskPoint(to)}
            highlighted={from.id === hoveredTaskId || to.id === hoveredTaskId}
          />
        )
      })
    })

    schedTasks.forEach(t => {
      const position = positions.get(t.id)
      const depth = position.depth

      const updateDates = (startDate: number, deadline: number) => {
        setChangedTask?.(
          {
            ...t,
            deadline: deadline || t.deadline,
            startDate: startDate || t.startDate
          },
          0
        )
        applyChange(() => {
          setTimeout(() => {
            requestAnimationFrame(() => {
              setChangedTask?.(undefined, undefined)
            })
          }, 300)
        })
      }
      const enter = () => {
        setHighlight?.(
          [
            extractStickerState(t.startDate) || extractStickerState(t.deadline),
            extractStickerState(t.deadline)
          ],
          t.id
        )
      }
      const leave = () => {
        setHighlight?.(undefined, undefined)
      }

      taskItems.push(
        <Task
          task={t}
          click={taskClick(t.id)}
          key={t.id}
          startTime={start}
          top={depth * (dayHeight + dayHeightSpace) + 24}
          width={dayWidth}
          height={dayHeight}
          dragStart={dragStart(t)}
          dragEnd={dragEnd}
          hide={changedTask && changedTask.id === t.id}
          hideHint={!!changedTask}
          getClientLeft={getClientLeft}
          updateDates={updateDates}
          mouseEnter={enter}
          mouseLeave={leave}
          dependencyHover={hoveredDependencies.has(t.id)}
          zoom={zoom}
        />
      )
    })

    if (changedTask) {
      taskItems.push(
        <Task
          task={changedTask}
          click={Util.noop}
          key={'dragged'}
          startTime={start}
          top={changedTask.timelineDepth * (dayHeight + dayHeightSpace) + 24}
          width={dayWidth}
          height={dayHeight}
          dragged={true}
          hideHint={true}
          getClientLeft={getClientLeft}
          updateDates={Util.noop}
          dependencyHover={false}
          zoom={zoom}
        />
      )
    }

    const move = !changedTask
      ? Util.noop
      : (e: React.DragEvent) => {
          const {clientX, clientY} = e
          moveDebouncer.runButNotOften(() => {
            requestAnimationFrame(() => {
              const rect = container.getBoundingClientRect()
              const scrollTop = tasksContainer ? tasksContainer.scrollTop : 0
              const x = rect.left
              const y = rect.top
              const newDeadline = Time.middleOfDay(
                start +
                  Math.round(
                    (day *
                      (container.scrollLeft +
                        clientX -
                        dragPoint -
                        dayWidth -
                        x)) /
                      dayWidth
                  )
              )
              const depth = Math.max(
                0,
                Math.round(
                  (clientY - y + scrollTop - 49) /
                    (dayHeight + dayHeightSpace) -
                    1
                )
              )
              const shift = isSetSticker(changedTask.deadline)
                ? newDeadline - Time.middleOfDay(changedTask.deadline)
                : 0

              const newStart = isSetSticker(changedTask.startDate)
                ? Time.middleOfDay(changedTask.startDate + shift)
                : undefined

              if (
                depth !== changedTask.timelineDepth ||
                (Time.middleOfDay(latestMoveDeadline) !==
                  Time.middleOfDay(newDeadline) &&
                  (!isSetSticker(changedTask.deadline) ||
                    Time.middleOfDay(changedTask.deadline) !==
                      Time.middleOfDay(newDeadline)))
              ) {
                latestMoveDeadline = newDeadline

                setChangedTask?.(
                  {
                    ...changedTask,
                    deadline: newDeadline,
                    startDate: newStart,
                    timelineDepth: depth
                  },
                  undefined
                )
              }
            })
          })
        }

    const allUnsched = Util.removeDuplicateIds(tasks)
      .filter(t => !isSetSticker(t.deadline) && !t.archived)
      .sort(
        (a, b) =>
          +!!a.completeTime - +!!b.completeTime || a.createTime - b.createTime
      )
    const [unscheduledOpen, setUnschedOpen] = React.useState(
      allUnsched.length > 0
    )

    const unscheduled = allUnsched.slice(0, maxUnscheduled).map(t => {
      const columnColor = getColumnColorHex(
        getColumnColorNumber(
          TaskExt.getById(TaskExt.findContainingColumnId(t.id))
        ),
        globals.theme,
        true
      )
      return (
        <DivWithHint
          className={bem('unsched-task', {
            withBg: !!columnColor,
            completed: !!t.completeTime
          })}
          content={t.title}
          key={t.id}
          draggable={true}
          onDragStart={dragStart(t)}
          onDragEnd={dragEnd}
          onClick={taskClick(t.id)}
          style={{
            backgroundColor: columnColor,
            width: '180px'
          }}
          hideHint={t.title.length * 7 + (t.assigned ? 20 : 0) < 180}
        >
          <TaskAssigned assigned={t.assigned} />
          <div className={bem('unsched-txt')}>{t.title}</div>
        </DivWithHint>
      )
    })
    const toggle = () => {
      setUnschedOpen(!unscheduledOpen)
    }
    const moreUnsched = () => setMaxUnscheduled?.(maxUnscheduled + 50)

    const timelineGrab = (e: React.MouseEvent) => {
      if (!container || !tasksContainer) {
        return
      }
      const grabClass = 'timeline--document-grab'
      const x = e.clientX
      const y = e.clientY
      const scroll = container.scrollLeft
      const scrollTop = tasksContainer.scrollTop
      let finish = false
      grabbing = true
      setTimeout(() => {
        if (!finish) {
          window.document.body.classList.add(grabClass)
        }
      }, 200)
      const grabMove = (ev: MouseEvent) => {
        const ex = ev.clientX
        const ey = ev.clientY
        moveDebouncer.runButNotOften(() =>
          requestAnimationFrame(() => {
            if (container) {
              container.scrollLeft = scroll + x - ex
            }
            if (tasksContainer) {
              tasksContainer.scrollTop = scrollTop + y - ey
            }
          })
        )
      }
      const grabUp = () => {
        finish = true
        grabbing = false
        window.document.body.classList.remove(grabClass)
        window.document.removeEventListener('mousemove', grabMove)
        window.document.removeEventListener('mouseup', grabUp)
        onScroll()
      }
      window.document.addEventListener('mousemove', grabMove)
      window.document.addEventListener('mouseup', grabUp)
    }

    return (
      <div className={bem({unscheduledOpen})}>
        {!withoutUnscheduled && (
          <div className={bem('unscheduled-toggle')} onClick={toggle}>
            {Lang.msg.timeline.toggleUnscheduled}
          </div>
        )}
        <div className={bem('cnt')} ref={setScroll} onScroll={onScroll}>
          <div
            className={bem('line')}
            style={{width: days * dayWidth + 'px'}}
            onMouseDown={timelineGrab}
            ref={tasksLineRef}
          >
            <div className={bem('header')}>
              <div className={bem('months')}>{months}</div>
              <div className={bem('dates')}>
                {highlightedArea}
                {dates}
              </div>
            </div>
            <div className={bem('body')} onDragOver={move}>
              {!!changedTask && <div className={bem('overlay')} />}
              <div className={bem('body-bg')}>
                {weekends}
                {verticalLines}
                <div className={bem('today')} style={{left: todayPos}} />
              </div>
              <div className={bem('tasks')} ref={tasksContainerRef}>
                <svg className={bem('svg')}>{arrows}</svg>
                {taskItems}
              </div>
            </div>
          </div>
        </div>
        {!withoutUnscheduled && unscheduledOpen && (
          <div className={bem('unscheduled')}>
            {unscheduled}
            {allUnsched.length > maxUnscheduled && (
              <div className={bem('more')}>
                {Lang.msg.moreTasks} <b>{allUnsched.length - maxUnscheduled}</b>
                <div className={bem('more-btn')} onClick={moreUnsched}>
                  {Lang.msg.showMore2}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    )
  }
)
